"use client";

import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface NavigationItem {
  label: string;
  href?: string;
}

interface NavigationBreadcrumbProps {
  items: NavigationItem[];
  onBack?: () => void;
  className?: string;
}

export function NavigationBreadcrumb({
  items,
  onBack,
  className = "",
}: NavigationBreadcrumbProps) {
  const navigate = useNavigate();

  // If we have 2 or fewer items (root + current), show only back button
  // If we have more than 2 items, show breadcrumbs without back button
  const shouldShowBreadcrumbs = items.length > 2;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  if (!shouldShowBreadcrumbs) {
    // Show only back button for 2 or fewer items
    return (
      <div className={cn("flex items-center", className)}>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition rounded-lg"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>
    );
  }

  // Show breadcrumbs for more than 2 items
  return (
    <div className={cn("flex items-center gap-1", className)}>
      {items.map((item, index) => (
        <div key={index} className="flex items-center">
          {index > 0 && <span className="mx-2 text-muted-foreground">/</span>}
          {item.href ? (
            <button
              onClick={() => navigate(item.href!)}
              className={cn(
                "text-sm hover:text-foreground transition-colors",
                index === items.length - 1
                  ? "text-foreground font-medium cursor-default"
                  : "text-muted-foreground hover:underline",
              )}
            >
              {item.label}
            </button>
          ) : (
            <span
              className={cn(
                "text-sm",
                index === items.length - 1
                  ? "text-foreground font-medium"
                  : "text-muted-foreground",
              )}
            >
              {item.label}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
